import config from '@plone/volto/registry';
import { toBackendLang } from '@plone/volto/helpers/Utils/Utils';

import { localizeCaRelativePath } from './localizedSectionPath';

/**
 * App path for a language home URL (browser route).
 * Uses the language root, e.g. /ca, /es, /en.
 */
export const getLanguageHomePath = (language) => {
  const lang = toBackendLang(
    language || config.settings.defaultLanguage || 'ca',
  );
  const homepagePath = config.settings.homepagePath;

  if (homepagePath) {
    return `/${lang}/${homepagePath}`;
  }

  return `/${lang}`;
};

/**
 * App path for editing the homepage content object (pagina-inicial).
 */
export const getHomepageEditPath = (language) => {
  const lang = toBackendLang(
    language || config.settings.defaultLanguage || 'ca',
  );
  const contentId =
    config.settings.homepageContentId ||
    config.settings.homepagePath ||
    'pagina-inicial';

  return `/${lang}/${contentId}/edit`;
};

/**
 * Whether the app pathname is a language home URL (e.g. /ca, /es, /en).
 */
export const isLanguageHomePath = (pathname) => {
  const normalized = (pathname || '').replace(/\/+$/, '') || '/';
  const supported = config.settings.supportedLanguages || [];

  return supported.some((lang) => normalized === `/${lang}`);
};

/**
 * API path used to load the header navigation tree.
 * Uses the active language root; falls back to default language.
 */
export const getNavigationRootPath = (pathname, locale) => {
  const supported = config.settings.supportedLanguages || [];
  const defaultLang = toBackendLang(config.settings.defaultLanguage || 'ca');
  const segments = (pathname || '').split('/').filter(Boolean);
  const pathLang = segments[0];

  if (pathLang && supported.includes(pathLang)) {
    return `/${toBackendLang(pathLang)}`;
  }

  if (locale) {
    const localeLang = toBackendLang(locale);
    if (supported.includes(localeLang)) {
      return `/${localeLang}`;
    }
  }

  return `/${defaultLang}`;
};

/**
 * Active content language from URL segment, Redux locale, or site default.
 */
export const getActiveLanguage = (pathname, locale) => {
  const supported = config.settings.supportedLanguages || [];
  const defaultLang = toBackendLang(config.settings.defaultLanguage || 'ca');
  const segments = (pathname || '').split('/').filter(Boolean);
  const pathLang = segments[0];

  if (pathLang && supported.includes(pathLang)) {
    return toBackendLang(pathLang);
  }

  if (locale) {
    const localeLang = toBackendLang(locale);
    if (supported.includes(localeLang)) {
      return localeLang;
    }
  }

  return defaultLang;
};

/**
 * Build an app path prefixed with the active language (e.g. /es/la-clinica/...).
 */
export const getLanguagePrefixedPath = (pathname, locale, relativePath) => {
  const lang = getActiveLanguage(pathname, locale);
  const path = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
  return localizeCaRelativePath(lang, path);
};
